import { useContext, type ReactNode } from "react"

import { ActionToolbar, type ToolbarItem } from "@coval/ui"

import { FilterBarScope } from "@/components/filters/ActiveFilterBar"
import { FilterBarContext } from "@/contexts/FilterBarContext"
import { TableDisplayContext } from "@/contexts/TableDisplayContext"

import { TableDisplaySlot } from "./TableDisplayScope"

const CONTROL_ORDER = ["search", "filter", "tags", "display", "view-mode"]

/** Keeps browsing controls together inside the toolbar's responsive layout. */
export function TableBrowseToolbar({
  items,
  filter,
}: {
  items: ToolbarItem[]
  filter?: ReactNode
}) {
  const displayScope = useContext(TableDisplayContext)
  const controls: ToolbarItem[] = [...items]
  if (filter) {
    controls.push({
      type: "custom",
      id: "filter",
      full: filter,
      compact: filter,
    })
  }
  if (displayScope && !controls.some((item) => item.id === "display")) {
    controls.push({
      type: "custom",
      id: "display",
      full: <TableDisplaySlot />,
      compact: <TableDisplaySlot />,
      // Measurement must not register a second portal target.
      measureFull: <span className="block size-8" />,
      measureCompact: <span className="block size-8" />,
    })
  }
  const position = (id: string) => {
    const index = CONTROL_ORDER.indexOf(id)
    return index < 0 ? CONTROL_ORDER.length : index
  }
  controls.sort((a, b) => position(a.id) - position(b.id))
  const measuredControls = controls.map((item) =>
    item.type === "custom"
      ? {
          ...item,
          measureFull: (
            <FilterBarContext.Provider value={null}>
              {item.measureFull ?? item.full}
            </FilterBarContext.Provider>
          ),
          measureCompact: (
            <FilterBarContext.Provider value={null}>
              {item.measureCompact ?? item.compact}
            </FilterBarContext.Provider>
          ),
        }
      : item,
  )
  return (
    <FilterBarScope className="min-w-0 w-full">
      <ActionToolbar items={measuredControls} align="start" />
    </FilterBarScope>
  )
}
